import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { calculatePopoverPosition } from '../components/Panel/lib/panelPosition';

export const usePopoverPosition = ({ isFixed, position, rerender, isOpen, onClose }) => {
  const [panelFixedPosition, setPanelFixedPosition] = useState({});
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelWidth = panelRef?.current?.getBoundingClientRect()?.width || 140;

  // Position calculation
  const updatePopoverPosition = useCallback(() => {
    if (!isFixed || !buttonRef.current || !panelRef?.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const panelRect = panelRef.current.getBoundingClientRect();
    const newPosition = calculatePopoverPosition({ buttonRect, panelRect, panelWidth, position });

    setPanelFixedPosition(newPosition);
  }, [isFixed, position, rerender]);

  // Layout effects for positioning
  useLayoutEffect(() => {
    if (isOpen) {
      updatePopoverPosition();
    }
  }, [isOpen, updatePopoverPosition, rerender]);

  useLayoutEffect(() => {
    if (isFixed && panelRef?.current) {
      const handleResize = () => onClose();

      const handleScroll = (event) => {
        if (
          (panelRef?.current &&
            event?.target instanceof Node &&
            panelRef?.current?.contains(event.target)) ||
          event?.target?.classList?.contains('select__menu-list')
        )
          return;
        updatePopoverPosition();
      };

      window.addEventListener('resize', handleResize, true);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize, true);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [updatePopoverPosition, isFixed, onClose]);

  // Handle panel ref callback to ensure position is calculated when panel is first rendered
  const handlePanelRef = useCallback(
    (node) => {
      if (node && isFixed) {
        panelRef.current = node;
        // Use requestAnimationFrame to ensure the panel is fully rendered
        requestAnimationFrame(() => {
          updatePopoverPosition();
        });
      } else if (!isFixed) {
        panelRef.current = node;
      }
    },
    [isFixed, updatePopoverPosition]
  );

  return {
    panelFixedPosition,
    buttonRef,
    panelRef,
    handlePanelRef,
    updatePopoverPosition
  };
};
